// EvStockPdfExport.js
// Usage: import { exportEvStockToPdf } from "./EvStockPdfExport";

import jsPDF     from "jspdf";
import autoTable from "jspdf-autotable";

const fmt = (v) =>
  v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const fmtPrice = (v) =>
  v != null ? `₹${Number(v).toLocaleString("en-IN")}` : "—";

const VEHICLE_LABEL = { E_RICKSHAW: "E-Rickshaw", SCOOTY: "Scooty" };

/**
 * exportEvStockToPdf
 * @param {Array}  groupedData  - [{ location, bikes[], total }]
 * @param {Object} filters      - active filter state
 * @param {String} label        - readable label shown in header
 */
export const exportStockToPdf = (groupedData, filters = {}, label = "All EV Stock") => {
  const doc   = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  /* ── PAGE HEADER ── */
  const addHeader = (title) => {
    doc.setFillColor(0, 105, 92);          // teal
    doc.rect(0, 0, pageW, 14, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(title, 10, 9);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated: ${new Date().toLocaleString("en-IN")}`, pageW - 10, 9, { align: "right" });
    doc.setTextColor(0, 0, 0);
  };

  /* ── PAGE FOOTER ── */
  const addFooter = () => {
    doc.setFontSize(7);
    doc.setTextColor(150);
    doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageW / 2, pageH - 4, { align: "center" });
    doc.setTextColor(0, 0, 0);
  };

  const reportTitle = `EV Stock Report — ${label}`;

  /* ══════════════════════════════════════════════════════
     PAGE 1: SUMMARY TABLE
  ══════════════════════════════════════════════════════ */
  addHeader(reportTitle);

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 105, 92);
  doc.text("Summary by Location", 10, 22);
  doc.setTextColor(0, 0, 0);

  const summaryBody = groupedData.map((loc) => {
    const eRick   = loc.bikes.filter((b) => b.vehicleType === "E_RICKSHAW").length;
    const scooty  = loc.bikes.filter((b) => b.vehicleType === "SCOOTY").length;
    const nw      = loc.bikes.filter((b) => b.source === "NEW").length;
    const ex      = loc.bikes.filter((b) => b.source === "EXCHANGE").length;
    const inStock = loc.bikes.filter((b) => b.status === "IN_STOCK").length;
    const sold    = loc.bikes.filter((b) => b.status === "SOLD").length;
    
    return [
      loc.location, loc.total, eRick, scooty, nw, ex,
      inStock, sold, 
    ];
  });

  // Grand total
  const gtTotal  = groupedData.reduce((s, l) => s + l.total, 0);
  const gtERick  = groupedData.reduce((s, l) => s + l.bikes.filter((b) => b.vehicleType === "E_RICKSHAW").length, 0);
  const gtScooty = groupedData.reduce((s, l) => s + l.bikes.filter((b) => b.vehicleType === "SCOOTY").length, 0);
  const gtNew    = groupedData.reduce((s, l) => s + l.bikes.filter((b) => b.source === "NEW").length, 0);
  const gtEx     = groupedData.reduce((s, l) => s + l.bikes.filter((b) => b.source === "EXCHANGE").length, 0);
  const gtStock  = groupedData.reduce((s, l) => s + l.bikes.filter((b) => b.status === "IN_STOCK").length, 0);
  const gtSold   = groupedData.reduce((s, l) => s + l.bikes.filter((b) => b.status === "SOLD").length, 0);
  

  summaryBody.push(["GRAND TOTAL", gtTotal, gtERick, gtScooty, gtNew, gtEx, gtStock, gtSold,]);

  autoTable(doc, {
    startY: 26,
    head: [["Location", "Total", "E-Rickshaw", "Scooty", "NEW", "EXCHANGE", "IN STOCK", "SOLD"]],
    body: summaryBody,
    theme: "grid",
    headStyles: { fillColor: [0, 105, 92], textColor: 255, fontStyle: "bold", fontSize: 8.5 },
    bodyStyles: { fontSize: 8.5 },
    alternateRowStyles: { fillColor: [240, 250, 248] },
    didParseCell: (data) => {
      if (data.row.index === summaryBody.length - 1) {
        data.cell.styles.fillColor  = [0, 77, 64];
        data.cell.styles.textColor  = 255;
        data.cell.styles.fontStyle  = "bold";
      }
    },
    margin: { left: 10, right: 10 },
    didDrawPage: addFooter,
  });

  /* ══════════════════════════════════════════════════════
     PER-LOCATION: TWO TABLES PER LOCATION
     Table A — Core vehicle fields
     Table B — Battery / Warranty / Financial fields
  ══════════════════════════════════════════════════════ */
  groupedData.forEach((loc) => {
    if (!loc.bikes.length) return;

    /* ── TABLE A: Core info ── */
    doc.addPage();
    addHeader(reportTitle);

    doc.setFillColor(224, 247, 244);
    doc.roundedRect(10, 17, pageW - 20, 8, 2, 2, "F");
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 105, 92);
    doc.text(`📍 ${loc.location}  —  ${loc.total} vehicle(s)  [CORE INFO]`, 14, 22.5);
    doc.setTextColor(0, 0, 0);

    autoTable(doc, {
      startY: 27,
      columns: [
        { header: "#",           dataKey: "idx" },
        { header: "Sl No",       dataKey: "slNo" },
        { header: "Date",        dataKey: "date" },
        { header: "Type",        dataKey: "vehicleType" },
        { header: "Company",     dataKey: "companyName" },
        { header: "Model",       dataKey: "model" },
        { header: "Colour",      dataKey: "colour" },
        { header: "Engine No",   dataKey: "engine" },
        { header: "Chasis No",   dataKey: "chasis" },
        { header: "Invoice No",  dataKey: "invoiceNo" },
        { header: "Source",      dataKey: "source" },
        { header: "Status",      dataKey: "status" },
        { header: "Form-22",     dataKey: "form22Received" },
      ],
      body: loc.bikes.map((b, i) => ({
        idx:           i + 1,
        slNo:          b.slNo ?? "—",
        date:          fmt(b.date),
        vehicleType:   VEHICLE_LABEL[b.vehicleType] || b.vehicleType || "—",
        companyName:   b.companyName || "—",
        model:         b.model || "—",
        colour:        b.colour || "—",
        engine:        b.engine || "—",
        chasis:        b.chasis || "—",
        invoiceNo:     b.invoiceNo || "—",
        source:        b.source || "—",
        status:        b.status || "—",
        form22Received: b.form22Received ? "Yes" : "No",
      })),
      theme: "grid",
      headStyles: { fillColor: [0, 105, 92], textColor: 255, fontStyle: "bold", fontSize: 7 },
      bodyStyles: { fontSize: 7 },
      alternateRowStyles: { fillColor: [240, 250, 248] },
      columnStyles: {
        0:  { cellWidth: 8,  halign: "center" },
        1:  { cellWidth: 12 },
        2:  { cellWidth: 18 },
        3:  { cellWidth: 18 },
        4:  { cellWidth: 28 },
        5:  { cellWidth: 30 },
        6:  { cellWidth: 16 },
        7:  { cellWidth: 30 },
        8:  { cellWidth: 30 },
        9:  { cellWidth: 26 },
        10: { cellWidth: 16, halign: "center" },
        11: { cellWidth: 16, halign: "center" },
        12: { cellWidth: 14, halign: "center" },
      },
      didDrawPage: addFooter,
      margin: { left: 10, right: 10, top: 28 },
    });

    /* ── TABLE B: Battery / Financial ── */
    doc.addPage();
    addHeader(reportTitle);

    doc.setFillColor(224, 247, 244);
    doc.roundedRect(10, 17, pageW - 20, 8, 2, 2, "F");
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 105, 92);
    doc.text(`📍 ${loc.location}  —  ${loc.total} vehicle(s)  [BATTERY & FINANCIAL INFO]`, 14, 22.5);
    doc.setTextColor(0, 0, 0);

    autoTable(doc, {
      startY: 27,
      columns: [
        { header: "#",               dataKey: "idx" },
        { header: "Sl No",           dataKey: "slNo" },
        { header: "Model",           dataKey: "model" },
        { header: "Battery Type",    dataKey: "batteryType" },
        { header: "OAM Battery",     dataKey: "oamBattery" },
        { header: "Controller No",   dataKey: "controllerNo" },
        { header: "Ctrl Warranty",   dataKey: "controllerWarranty" },
        { header: "Motor Warranty",  dataKey: "motorWarranty" },
        { header: "Charger",         dataKey: "charger" },
        { header: "Charger Warranty",dataKey: "chargerWarranty" },
        { header: "Warranty Period", dataKey: "warrantyPeriod" },
        
        { header: "Transport ₹",     dataKey: "transportPrice" },
        { header: "Sale Date",       dataKey: "saleDate" },
        { header: "Remarks",         dataKey: "remarks" },
      ],
      body: loc.bikes.map((b, i) => ({
        idx:                i + 1,
        slNo:               b.slNo ?? "—",
        model:              b.model || "—",
        batteryType:        b.batteryType || "—",
        oamBattery:         b.oamBattery ? "Yes" : "No",
        controllerNo:       b.controllerNo || "—",
        controllerWarranty: fmt(b.controllerWarranty),
        motorWarranty:      fmt(b.motorWarranty),
        charger:            b.charger || "—",
        chargerWarranty:    fmt(b.chargerWarranty),
        warrantyPeriod:     fmt(b.warrantyPeriod),
        
        transportPrice:     fmtPrice(b.transportPrice),
        saleDate:           fmt(b.saleDate),
        remarks:            b.remarks || "—",
      })),
      theme: "grid",
      headStyles: { fillColor: [0, 77, 64], textColor: 255, fontStyle: "bold", fontSize: 7 },
      bodyStyles: { fontSize: 7 },
      alternateRowStyles: { fillColor: [240, 250, 248] },
      columnStyles: {
        0:  { cellWidth: 8,  halign: "center" },
        1:  { cellWidth: 12 },
        2:  { cellWidth: 26 },
        3:  { cellWidth: 18 },
        4:  { cellWidth: 14, halign: "center" },
        5:  { cellWidth: 22 },
        6:  { cellWidth: 18 },
        7:  { cellWidth: 18 },
        8:  { cellWidth: 18 },
        9:  { cellWidth: 20 },
        10: { cellWidth: 18 },
        11: { cellWidth: 20, halign: "right" },
        12: { cellWidth: 18, halign: "right" },
        13: { cellWidth: 18, halign: "right" },
        14: { cellWidth: 16 },
        15: { cellWidth: 28 },
      },
      didDrawPage: addFooter,
      margin: { left: 10, right: 10, top: 28 },
    });
  });

  /* ── FILENAME & SAVE ── */
  const parts = ["EV_Stock_Report"];
  if (filters.vehicleType) parts.push(filters.vehicleType);
  if (filters.location)    parts.push(filters.location);
  if (filters.month)       parts.push(`M${filters.month}`);
  if (filters.year)        parts.push(`Y${filters.year}`);
  if (filters.fromDate)    parts.push(`From-${filters.fromDate}`);
  if (filters.toDate)      parts.push(`To-${filters.toDate}`);
  parts.push(new Date().toISOString().split("T")[0]);

  doc.save(`${parts.join("_")}.pdf`);
};